import { radiansToDegrees, TLParentId, TLShapeId } from "@tldraw/tldraw";
import React, { useEffect, useState } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import useTooltipStyles from "./style";
import { getClosestContainer, hideSelectionTemporarily } from "../../utils";
import { VerticalDitributeIcon } from "components/icons/components/VerticalDitributeIcon";
import HorizontalDistributeIcon from "components/icons/components/HorizontalDistributeIcon";
import { TidyUpIcon } from "components/icons/components/TidyUpIcon";
import { KeyboardIcon } from "components/icons/components/KeyboardIcon";
import RotateIcon from "components/icons/components/RotateIcon";
import FlipVerticalIcon from "components/icons/components/FlipVerticalIcon";
import FlipHorizontalIcon from "components/icons/components/FlipHorizontalIcon";
import { OpacityIcon } from "components/icons/components/OpacityIcon";
import { BorderRadiusIcon } from "components/icons/components/BorderRadiusIcon";
import { useDragToAdjust } from "../hooks/useDragToAdjust";
import { runTidyUp } from "../utils/tidyUp";
import { RadiusCornersIcon } from "components/icons/components/RadiusCornersIcon";
import { CornerRadiusPopover, CornerValues, TriangleCornerValues } from "./CornerRadiusPopover";

interface RotateComponentProps {
  selectedShape: any;
  setSelectedShape: (shape: any) => void;
  editor: any;
  selectedShapeIds: any[];
  selectedShapes: any[];
}

const useDistributeMenuStyles = makeStyles({
  menuItem: {
    fontSize: 12,
    color: "#2E3860",
    "&:hover": {
      backgroundColor: "#E2E2FC !important",
    },
  },
});

const RotateComponent = ({ selectedShape, setSelectedShape, editor, selectedShapes }: RotateComponentProps) => {
  const [rotation, setRotation] = useState(selectedShape?.rotation);
  const [opacity, setOpacity] = useState((selectedShape?.props?.transparency || 0) * 100);
  const [radius, setRadius] = useState(selectedShape?.props?.rounded || 0);
  // Per-corner radii anchor for the popover. Click the small "corners"
  // icon to the right of the radius input → popover opens here.
  const [cornerAnchorEl, setCornerAnchorEl] = useState<null | HTMLElement>(null);
  const [distributeAnchorEl, setDistributeAnchorEl] = useState<null | HTMLElement>(null);
  const [distributeDirection, setDistributeDirection] = useState<"vertical" | "horizontal">("vertical");
  const [distributeMode, setDistributeMode] = useState<"vertical" | "horizontal" | "tidy">("vertical");
  const classes = useTooltipStyles();
  const distributeMenuClasses = useDistributeMenuStyles();
  const frontSaveZoneId = "shape:frontBorderDashLine" as TLParentId;
  const backSaveZoneId = "shape:backBorderDashLine" as TLParentId;

  const median = (values: number[]) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRotation = Number(e.target.value);
    const rotationInRadians = (newRotation * Math.PI) / 180;
    editor.rotateShapesBy([selectedShape.id], rotationInRadians - selectedShape.rotation);
    setRotation(rotationInRadians);
    setSelectedShape({ ...selectedShape, rotation: rotationInRadians });
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedShape) return;
    const newOpacity = Math.min(100, Math.max(0, Number(e.target.value) || 0));
    setOpacity(newOpacity);

    // Update all selected shapes - convert from 0-100 to 0-1 range
    const transparencyValue = newOpacity / 100;
    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty("transparency")) return;
      const updatedProps = { ...shape.props, transparency: transparencyValue };
      editor.updateShape({
        ...shape,
        isLocked: false,
        props: updatedProps,
      });
      if (shape.id === selectedShape.id) {
        setSelectedShape({ ...shape, props: updatedProps });
      }
    });
  };

  const shouldShowOpacity = () => {
    if (!selectedShape) return false;
    const shapeType = selectedShape?.type;
    // Show for images and shapes, but not for text or groups
    const imageTypes = ["image", "customImage"];
    const shapeTypes = ["customRect", "customEllipse", "customTriangle", "line", "arrow"];
    return imageTypes.includes(shapeType) || shapeTypes.includes(shapeType);
  };

  // The popover swaps to a 3-input variant when the selected shape is
  // a customTriangle (Polygon), since a triangle has 3 vertices, not 4
  // corners. Every helper below branches on this flag.
  const isTriangle = selectedShape?.type === "customTriangle";

  // Read the effective per-corner radii from a shape's props with the
  // same `roundedX || rounded` fallback the renderers use. Used both
  // by the popover (to seed its inputs) and by the "Mixed" detection
  // for the main input below.
  const readCornerValues = (shape: any): CornerValues => {
    const fallback = Number(shape?.props?.rounded || 0);
    const pick = (v: any) => (v === undefined || v === null || Number.isNaN(Number(v)) ? fallback : Number(v));
    return {
      tl: pick(shape?.props?.roundedTL),
      tr: pick(shape?.props?.roundedTR),
      br: pick(shape?.props?.roundedBR),
      bl: pick(shape?.props?.roundedBL),
    };
  };

  // Triangle variant — reads `roundedTop` / `roundedBR` / `roundedBL`
  // with the same uniform-fallback semantics. Note that BR/BL share
  // names with the rect variant (semantically equivalent — both are
  // bottom-right / bottom-left), so a user toggling between a rect
  // and a triangle keeps those corner radii consistent.
  const readTriangleCornerValues = (shape: any): TriangleCornerValues => {
    const fallback = Number(shape?.props?.rounded || 0);
    const pick = (v: any) => (v === undefined || v === null || Number.isNaN(Number(v)) ? fallback : Number(v));
    return {
      top: pick(shape?.props?.roundedTop),
      bl: pick(shape?.props?.roundedBL),
      br: pick(shape?.props?.roundedBR),
    };
  };

  const cornerValues: CornerValues = selectedShape ? readCornerValues(selectedShape) : { tl: 0, tr: 0, br: 0, bl: 0 };
  const triangleCornerValues: TriangleCornerValues = selectedShape
    ? readTriangleCornerValues(selectedShape)
    : { top: 0, bl: 0, br: 0 };

  // When the per-corner values disagree the main radius input shows
  // blank (placeholder "Mixed") — same Figma-style affordance. Typing
  // in that input still works and stamps the new value into every
  // per-corner field (see `handleRadiusChange` below).
  const cornersDiffer = isTriangle
    ? !(triangleCornerValues.top === triangleCornerValues.bl && triangleCornerValues.bl === triangleCornerValues.br)
    : !(
        cornerValues.tl === cornerValues.tr &&
        cornerValues.tr === cornerValues.br &&
        cornerValues.br === cornerValues.bl
      );

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedShape) return;
    const newRadius = Math.min(100, Math.max(0, Number(e.target.value) || 0));
    setRadius(newRadius);

    // Update all selected shapes. Writing the uniform `rounded` here
    // would only stick for shapes that have NO per-corner overrides —
    // any shape with even one per-corner field set would keep that
    // override and ignore the new value. To make the main input
    // behave like a true "set all corners" control we also stamp the
    // new value into every per-corner field the shape's renderer
    // actually reads: rect-shaped shapes (rect / image / checkbox /
    // xbox) get `roundedTL/TR/BR/BL`; triangle gets `roundedTop` +
    // `roundedBR` + `roundedBL`. We branch per shape so we don't
    // persist `roundedTop` on a rect (or `roundedTL/TR` on a
    // triangle) where it's dead data. Shapes without per-corner
    // support are skipped.
    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty("rounded")) return;
      const perCornerOverrides =
        shape.type === "customTriangle"
          ? {
              roundedTop: newRadius,
              roundedBR: newRadius,
              roundedBL: newRadius,
            }
          : {
              roundedTL: newRadius,
              roundedTR: newRadius,
              roundedBR: newRadius,
              roundedBL: newRadius,
            };
      const updatedShape = {
        ...shape,
        props: {
          ...shape.props,
          rounded: newRadius,
          ...perCornerOverrides,
        },
      };
      editor.updateShape(updatedShape);
      if (shape.id === selectedShape.id) {
        setSelectedShape(updatedShape);
      }
      hideSelectionTemporarily(updatedShape, editor);
    });
  };

  // Per-corner edit from the rect-variant popover. Writes the four
  // `roundedX` fields and also updates `rounded` to the TL value so
  // any code path that still reads only the uniform value (legacy
  // snapshots, exports) sees something reasonable.
  const handleCornersChange = (next: CornerValues) => {
    if (!selectedShape) return;
    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty("rounded")) return;
      const updatedShape = {
        ...shape,
        props: {
          ...shape.props,
          rounded: next.tl,
          roundedTL: next.tl,
          roundedTR: next.tr,
          roundedBR: next.br,
          roundedBL: next.bl,
        },
      };
      editor.updateShape(updatedShape);
      if (shape.id === selectedShape.id) {
        setSelectedShape(updatedShape);
        setRadius(next.tl);
      }
      hideSelectionTemporarily(updatedShape, editor);
    });
  };

  // Per-vertex edit from the triangle-variant popover. Same shape as
  // `handleCornersChange` but writes the 3 triangle-specific fields
  // (`roundedTop`/`roundedBR`/`roundedBL`). Also stamps `rounded` to
  // the Top value so legacy reads stay sane.
  const handleTriangleCornersChange = (next: TriangleCornerValues) => {
    if (!selectedShape) return;
    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty("rounded")) return;
      const updatedShape = {
        ...shape,
        props: {
          ...shape.props,
          rounded: next.top,
          roundedTop: next.top,
          roundedBL: next.bl,
          roundedBR: next.br,
        },
      };
      editor.updateShape(updatedShape);
      if (shape.id === selectedShape.id) {
        setSelectedShape(updatedShape);
        setRadius(next.top);
      }
      hideSelectionTemporarily(updatedShape, editor);
    });
  };

  const shouldShowCornerRadius = () => {
    if (!selectedShape) return false;
    // Check if shape has rounded property and is not customText, customDraw, or customHighlight
    return (
      selectedShape?.props?.rounded !== undefined &&
      selectedShape?.type !== "customText" &&
      selectedShape?.type !== "customDraw" &&
      selectedShape?.type !== "customHighlight"
    );
  };

  // Refs to avoid stale closures inside drag handlers
  const rotationRef = React.useRef(rotation);
  rotationRef.current = rotation;
  const opacityRef = React.useRef(opacity);
  opacityRef.current = opacity;
  const radiusRef = React.useRef(radius);
  radiusRef.current = radius;

  const angleDrag = useDragToAdjust(
    () => Math.round(((rotationRef.current * 180) / Math.PI) % 360),
    (deg) => {
      if (!selectedShape) return;
      // Set absolute rotation — `handleInputChange` uses `rotateShapesBy(delta)`
      // which accumulates incorrectly when many mousemoves fire within the
      // same React frame (closure reads stale `selectedShape.rotation`).
      const newRad = (deg * Math.PI) / 180;
      const latest = editor.getShape(selectedShape.id) ?? selectedShape;
      editor.updateShape({ ...latest, rotation: newRad });
      setRotation(newRad);
      setSelectedShape({ ...latest, rotation: newRad });
    },
    { step: 1 }
  );

  const opacityDrag = useDragToAdjust(
    () => Math.trunc(opacityRef.current),
    (pct) => handleOpacityChange({ target: { value: String(pct) } } as any),
    { min: 0, max: 100, step: 1 }
  );

  const radiusDrag = useDragToAdjust(
    () => Math.trunc(radiusRef.current),
    (r) => handleRadiusChange({ target: { value: String(r) } } as any),
    { min: 0, max: 100, step: 1 }
  );

  const handleDistributeClick = (event: React.MouseEvent<HTMLElement>) => {
    setDistributeAnchorEl(event.currentTarget);
  };

  const handleDistributeClose = () => {
    setDistributeAnchorEl(null);
  };

  const handleVerticalDistribute = () => {
    if (selectedShapes.length === 0 && !selectedShape) {
      console.warn("No selected shapes found");
      return;
    }

    const referenceShape = selectedShape || selectedShapes[0];
    if (!referenceShape) {
      console.warn("No valid reference shape found");
      return;
    }

    const closestContainer = getClosestContainer(frontSaveZoneId, backSaveZoneId, editor, referenceShape);

    if (!closestContainer) {
      console.warn("No container found for the reference shape");
      return;
    }

    let shapeIdsToDistribute: TLShapeId[] = [];

    if (selectedShapes.length > 1) {
      shapeIdsToDistribute = selectedShapes.filter((shape) => shape && shape.id).map((shape) => shape.id);
    } else {
      shapeIdsToDistribute = [referenceShape.id, closestContainer.id];
    }

    editor.distributeShapes(shapeIdsToDistribute, "vertical");
  };

  const handleHorizontalDistribute = () => {
    if (selectedShapes.length === 0 && !selectedShape) {
      console.warn("No selected shapes found");
      return;
    }

    const referenceShape = selectedShape || selectedShapes[0];
    if (!referenceShape) {
      console.warn("No valid reference shape found");
      return;
    }

    const closestContainer = getClosestContainer(frontSaveZoneId, backSaveZoneId, editor, referenceShape);

    if (!closestContainer) {
      console.warn("No container found for the reference shape");
      return;
    }

    let shapeIdsToDistribute: TLShapeId[] = [];

    if (selectedShapes.length > 1) {
      shapeIdsToDistribute = selectedShapes.filter((shape) => shape && shape.id).map((shape) => shape.id);
    } else {
      shapeIdsToDistribute = [referenceShape.id, closestContainer.id];
    }

    editor.distributeShapes(shapeIdsToDistribute, "horizontal");
  };

  // Lifted to `drawer/utils/tidyUp.ts` so the global Cmd/Ctrl+Alt+T
  // shortcut handler (in BottomToolPanel) can run the same logic even
  // when this component isn't mounted. Local wrapper just adds the
  // 2-shape minimum warning that's specific to the UI button.
  const handleTidyUp = () => {
    if (!selectedShapes || selectedShapes.length < 2) {
      console.warn("Select at least two shapes to tidy up");
      return;
    }
    runTidyUp(editor, selectedShapes);
  };

  const handleRotate90 = () => {
    // Support both single and multiple selection
    if (selectedShapes.length === 0 && !selectedShape) {
      return;
    }

    const deg90 = Math.PI / 2;

    if (selectedShapes.length > 1) {
      // Multiple shapes: rotate all of them around their collective center
      const selectionBounds = editor.getSelectionPageBounds();
      if (selectionBounds) {
        const center = {
          x: selectionBounds.x + selectionBounds.w / 2,
          y: selectionBounds.y + selectionBounds.h / 2,
        };
        const shapeIds = selectedShapes.map((shape) => shape.id);
        editor.rotateShapesBy(shapeIds, deg90, { center });
      }
    } else {
      // Single shape: rotate around its own center
      const shapeToRotate = selectedShape || selectedShapes[0];
      if (!shapeToRotate) return;

      editor.rotateShapesBy([shapeToRotate.id], deg90);
      const newRotation = shapeToRotate.rotation + deg90;
      setRotation(newRotation);
      setSelectedShape({ ...shapeToRotate, rotation: newRotation });
    }
  };

  const handleFlip = (operation: "horizontal" | "vertical") => {
    try {
      const shapesToFlip = selectedShapes.length > 0 ? selectedShapes : selectedShape ? [selectedShape] : [];
      if (shapesToFlip.length === 0) {
        console.warn("No shape selected for flipping");
        return;
      }

      const customShapesWithFlipProps = ["customRect", "customEllipse", "customTriangle"];
      // Collect shapes that need editor.flipShapes (not handled via direct prop updates)
      const flipShapeIds: string[] = [];

      for (const shape of shapesToFlip) {
        if (shape.type === "customText") {
          // For text shapes, update the flip properties directly
          const updatedShape = {
            ...shape,
            props: {
              ...shape.props,
              flipH: operation === "horizontal" ? !shape.props.flipH : shape.props.flipH,
              flipV: operation === "vertical" ? !shape.props.flipV : shape.props.flipV,
            },
          };
          editor.updateShape(updatedShape);
          if (shape.id === selectedShape?.id) {
            setSelectedShape(updatedShape);
          }
        } else if (customShapesWithFlipProps.includes(shape.type)) {
          // For custom shapes that have flipX/flipY in props, update them directly
          const currentFlipX = shape.props?.flipX || false;
          const currentFlipY = shape.props?.flipY || false;
          const updatedShape = {
            ...shape,
            props: {
              ...shape.props,
              flipX: operation === "horizontal" ? !currentFlipX : currentFlipX,
              flipY: operation === "vertical" ? !currentFlipY : currentFlipY,
            },
          };
          editor.updateShape(updatedShape);
          if (shape.id === selectedShape?.id) {
            setSelectedShape(updatedShape);
          }
        } else {
          // For other shapes (including images), collect for batch flip
          flipShapeIds.push(shape.id);
        }
      }

      // Batch flip all non-custom shapes at once
      if (flipShapeIds.length > 0) {
        try {
          const flipShapeObjects = flipShapeIds.map((id) => editor.getShape(id)).filter(Boolean);
          editor.flipShapes(flipShapeObjects, operation);
        } catch (error) {
          console.error("Error flipping shapes:", error);
          // Fallback: try updating flipX/flipY props individually
          for (const id of flipShapeIds) {
            const shape = editor.getShape(id);
            if (!shape) continue;
            const shapesWithoutFlipProps = ["arrow", "line"];
            if (shapesWithoutFlipProps.includes(shape.type)) continue;
            const currentFlipX = shape.props?.flipX || false;
            const currentFlipY = shape.props?.flipY || false;
            editor.updateShape({
              ...shape,
              props: {
                ...shape.props,
                flipX: operation === "horizontal" ? !currentFlipX : currentFlipX,
                flipY: operation === "vertical" ? !currentFlipY : currentFlipY,
              },
            });
          }
        }
      }

      if (selectedShape) {
        hideSelectionTemporarily(selectedShape, editor);
      }
    } catch (error) {
      console.error("Error in handleFlip:", error);
    }
  };

  useEffect(() => {
    setRotation(selectedShape?.rotation);
    setOpacity((selectedShape?.props?.transparency || 0) * 100);
    setRadius(selectedShape?.props?.rounded || 0);
  }, [selectedShape]);

  // Tidy-up keydown moved to BottomToolPanel so the shortcut fires even
  // when this component isn't mounted (it lives in the right sidebar
  // and only renders for certain selections). Local React state for
  // the distribute mode UI is still updated via the
  // `flyer:distribute-mode` event the BottomToolPanel handler
  // dispatches before invoking `runTidyUp`.

  useEffect(() => {
    const handleDistributeModeEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ mode?: "vertical" | "horizontal" | "tidy" }>;
      const mode = customEvent.detail?.mode;
      if (!mode) return;
      setDistributeMode(mode);
      if (mode === "vertical" || mode === "horizontal") {
        setDistributeDirection(mode);
      }
    };

    window.addEventListener("flyer:distribute-mode", handleDistributeModeEvent as EventListener);
    return () => {
      window.removeEventListener("flyer:distribute-mode", handleDistributeModeEvent as EventListener);
    };
  }, []);

  const displayRotation = (rotation: number) => {
    return Math.round(radiansToDegrees(rotation % (2 * Math.PI)) * 100) / 100;
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {/* Rotation and Distribute group */}
        <div
          className="flex items-center"
          style={{
            border: "1px solid #E4E4EA",
            borderRadius: "4px",
            // Anchor the wrapper to a fixed width so the matching
            // Opacity group below (single cell) can target the same
            // total width without depending on icon size / content
            // intrinsics. 100px = 3 × (icon 16 + padding 16) + 2px
            // inner separators + 2px outer border, matching the
            // alignment row's natural width above. See the same
            // `width: 100px` on the opacity wrapper.
            width: "100px",
            boxSizing: "border-box",
          }}
        >
          {/* Type rotation - takes 2 cells width */}
          <Tooltip title="Type rotation" arrow classes={{ tooltip: classes.tooltip }}>
            <div
              style={{
                paddingLeft: "8px",
                paddingRight: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRight: "1px solid #E4E4EA",
                minWidth: "50px",
                height: "32px",
                flex: 1,
              }}
              className="hover:bg-[#E2E2FC] transition-colors"
            >
              <div className="relative flex items-center" style={{ width: "49px" }}>
                <span
                  onMouseDown={angleDrag.onMouseDown}
                  style={{ cursor: "ew-resize", display: "inline-flex" }}
                  title="Drag to adjust angle"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4.06729 13.295C3.68607 13.295 3.3634 13.163 3.09929 12.8988C2.83518 12.6347 2.70312 12.3121 2.70312 11.9308V4.01568C2.70312 3.7319 2.83068 3.53468 3.08579 3.42401C3.3409 3.31323 3.56462 3.35401 3.75696 3.54634L5.00062 4.79001L4.29296 5.49768L4.68263 5.88734L5.39029 5.17968L6.96979 6.75918L6.26213 7.46684L6.65179 7.85651L7.35946 7.14884L8.96463 8.75401L8.25696 9.46168L8.64663 9.85134L9.35429 9.14368L10.9338 10.7232L10.2261 11.4308L10.616 11.8207L11.3236 11.113L12.4646 12.254C12.6526 12.442 12.6913 12.6625 12.5806 12.9155C12.47 13.1685 12.2748 13.295 11.9953 13.295H4.06729ZM4.39546 11.8078H9.72363L4.19029 6.27451V11.6027C4.19029 11.6626 4.20951 11.7117 4.24796 11.7502C4.2864 11.7886 4.33557 11.8078 4.39546 11.8078Z"
                      fill="#1A2547"
                    />
                  </svg>
                </span>
                <div className="relative flex-1 flex items-center gap-0">
                  <input
                    type={"number"}
                    value={`${Math.trunc(displayRotation(rotation))}`}
                    onChange={handleInputChange}
                    className="w-[38px] h-6 text-[12px] text-[#707087] text-start pl-2 pr-0 no-arrows border-none outline-none bg-transparent flex-1"
                  />
                  <span className="text-[#707087] text-[12px] leading-none -ml-[2px]">°</span>
                </div>
              </div>
            </div>
          </Tooltip>

          {/* Rotate 90° */}
          <Tooltip title="Rotate 90°" arrow classes={{ tooltip: classes.tooltip }}>
            <div
              onClick={handleRotate90}
              style={{
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
            >
              <RotateIcon />
            </div>
          </Tooltip>
        </div>

        {/* Distribute and Flip group */}
        <div
          className="flex items-center"
          style={{
            border: "1px solid #E4E4EA",
            borderRadius: "4px",
          }}
        >
          {/* Flip Horizontal */}
          <Tooltip title="Flip Horizontally" arrow classes={{ tooltip: classes.tooltip }}>
            <div
              onClick={() => handleFlip("horizontal")}
              style={{
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRight: "1px solid #E4E4EA",
              }}
              className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
            >
              <FlipHorizontalIcon />
            </div>
          </Tooltip>
          {/* Flip Vertical */}
          <Tooltip title="Flip Vertically" arrow classes={{ tooltip: classes.tooltip }}>
            <div
              onClick={() => handleFlip("vertical")}
              style={{
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
            >
              <FlipVerticalIcon />
            </div>
          </Tooltip>
          {/* Vertical Distribute - takes 1 cell width */}
          <Tooltip title="Distribute" arrow classes={{ tooltip: classes.tooltip }}>
            <div
              onClick={handleDistributeClick}
              style={{
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderLeft: "1px solid #E4E4EA",
              }}
              className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
            >
              {distributeMode === "tidy" ? (
                <TidyUpIcon />
              ) : distributeMode === "vertical" ? (
                <VerticalDitributeIcon />
              ) : (
                <HorizontalDistributeIcon />
              )}
            </div>
          </Tooltip>

          <Menu
            anchorEl={distributeAnchorEl}
            open={Boolean(distributeAnchorEl)}
            onClose={handleDistributeClose}
            keepMounted
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            MenuListProps={{ disablePadding: true }}
            PaperProps={{
              style: {
                borderRadius: 8,
                border: "1px solid #E2E2FC",
                color: "#2E3860",
                fontSize: 12,
                padding: 0,
              },
            }}
          >
            <MenuItem
              className={distributeMenuClasses.menuItem}
              onClick={() => {
                handleDistributeClose();
                handleTidyUp();
                setDistributeMode("tidy");
              }}
              style={{
                paddingTop: 12,
              }}
            >
              <div className="flex items-center justify-between w-full gap-6">
                <div className="flex items-center gap-2">
                  <TidyUpIcon />
                  <span>Tidy up</span>
                </div>
                <div className="flex items-center gap-2 text-[12px] text-[#AEAFC7]">
                  <KeyboardIcon strokeColor="#AEAFC7" />
                  <span>Ctrl + ⌥ + T</span>
                </div>
              </div>
            </MenuItem>
            <MenuItem
              className={distributeMenuClasses.menuItem}
              onClick={() => {
                handleDistributeClose();
                setDistributeDirection("vertical");
                setDistributeMode("vertical");
                handleVerticalDistribute();
              }}
            >
              <div className="flex items-center justify-between w-full gap-6">
                <div className="flex items-center gap-2">
                  <VerticalDitributeIcon />
                  <span>Vertical distribute</span>
                </div>
                <div className="flex items-center gap-2 text-[12px] text-[#AEAFC7]">
                  <KeyboardIcon strokeColor="#AEAFC7" />
                  <span>Ctrl + ⌥ + V</span>
                </div>
              </div>
            </MenuItem>
            <MenuItem
              className={distributeMenuClasses.menuItem}
              onClick={() => {
                handleDistributeClose();
                setDistributeDirection("horizontal");
                setDistributeMode("horizontal");
                handleHorizontalDistribute();
              }}
              style={{
                paddingBottom: 12,
              }}
            >
              <div className="flex items-center justify-between w-full gap-6">
                <div className="flex items-center gap-2">
                  <HorizontalDistributeIcon />
                  <span>Horizontal distribute</span>
                </div>
                <div className="flex items-center gap-2 text-[12px] text-[#AEAFC7]">
                  <KeyboardIcon strokeColor="#AEAFC7" />
                  <span>Ctrl + ⌥ + H</span>
                </div>
              </div>
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Opacity and Corner radius row - only visible for images and shapes */}
      {(shouldShowOpacity() || shouldShowCornerRadius()) && (
        <div className="flex items-center gap-2">
          {/* Opacity component */}
          {shouldShowOpacity() && (
            <div
              className="flex items-center"
              style={{
                border: "1px solid #E4E4EA",
                borderRadius: "4px",
                // Mirrors the Rotate group's `width: 100px` above so
                // Opacity's outer box matches the combined width of
                // [rotate-input + rotate-90 button] exactly, which
                // in turn equals the alignment row's natural width.
                // The inner cell stretches via `flex: 1` to fill.
                width: "100px",
                boxSizing: "border-box",
              }}
            >
              <Tooltip title="Opacity" arrow classes={{ tooltip: classes.tooltip }}>
                <div
                  style={{
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "50px",
                    height: "32px",
                    flex: 1,
                  }}
                  className="hover:bg-[#E2E2FC] transition-colors"
                >
                  <div className="relative flex items-center flex-1">
                    <span
                      onMouseDown={opacityDrag.onMouseDown}
                      style={{ cursor: "ew-resize", display: "inline-flex" }}
                      title="Drag to adjust opacity"
                    >
                      <OpacityIcon />
                    </span>
                    <div className="relative flex-1 flex items-center">
                      <input
                        type={"number"}
                        value={`${Math.trunc(opacity)}`}
                        onChange={handleOpacityChange}
                        className="w-[38px] h-6 text-[12px] text-[#707087] text-start pl-2 pr-0 no-arrows border-none outline-none bg-transparent flex-1"
                        min={0}
                        max={100}
                      />
                      <span className="text-[#707087] text-[12px] leading-none -ml-[2px]">%</span>
                    </div>
                  </div>
                </div>
              </Tooltip>
            </div>
          )}

          {/* Corner radius component */}
          {shouldShowCornerRadius() && (
            <div
              className="flex items-center"
              style={{
                border: "1px solid #E4E4EA",
                borderRadius: "4px",
              }}
            >
              <Tooltip title="Corner radius" arrow classes={{ tooltip: classes.tooltip }}>
                <div
                  style={{
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "50px",
                    height: "32px",
                  }}
                  className="hover:bg-[#E2E2FC] transition-colors"
                >
                  <div className="relative flex items-center" style={{ width: "40px" }}>
                    <span
                      onMouseDown={radiusDrag.onMouseDown}
                      style={{ cursor: "ew-resize", display: "inline-flex" }}
                      title="Drag to adjust corner radius"
                    >
                      <BorderRadiusIcon />
                    </span>
                    <div className="relative flex-1 flex items-center">
                      {/* When the per-corner values differ the main
                          input shows blank with a "Mix" placeholder
                          (short form — "Mixed" overflows the 38px
                          input column). Typing a number still stamps
                          that value into every per-corner field (see
                          `handleRadiusChange`). */}
                      <input
                        type={"number"}
                        value={cornersDiffer ? "" : `${Math.trunc(radius)}`}
                        placeholder={cornersDiffer ? "Mix" : undefined}
                        onChange={handleRadiusChange}
                        className="w-[38px] h-6 text-[12px] text-[#707087] text-start pl-2 pr-0 no-arrows border-none outline-none bg-transparent flex-1"
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                </div>
              </Tooltip>
            </div>
          )}

          {/* Per-corner border-radius editor. The trigger lives in its
              own bordered group so it visually pairs with the radius
              input to its left without being part of the same flex
              child (keeps the icon button at a fixed width). */}
          {shouldShowCornerRadius() && (
            <div
              className="flex items-center"
              style={{
                // "Selected" state mirrors the Mui-selected styling on
                // the format dropdown items: white fill, 1px navy
                // border (#2E3860), 4px radius. Triggered when the
                // popover is open OR any corner has a custom radius —
                // same condition as before, just a different visual.
                // No checkmark icon is overlaid; the navy outline is
                // the entire selected-state affordance.
                border: cornerAnchorEl || cornersDiffer ? "1px solid #2E3860" : "1px solid #E4E4EA",
                borderRadius: "4px",
              }}
            >
              <Tooltip title="Edit each corner" arrow classes={{ tooltip: classes.tooltip }}>
                <button
                  type="button"
                  onClick={(e) => setCornerAnchorEl(e.currentTarget)}
                  aria-label="Edit each corner radius"
                  data-cypress-id="corner-radius-popover-trigger"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    padding: 0,
                    border: "none",
                    // Background stays transparent so the selected
                    // state reads as the navy outline alone — matching
                    // the format dropdown's white-fill selected look.
                    // Hover still tints lavender for press feedback.
                    background: "transparent",
                    color: "#707087",
                    cursor: "pointer",
                  }}
                  className="hover:bg-[#E2E2FC] transition-colors"
                >
                  <RadiusCornersIcon style={{ width: 16, height: 16 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      )}

      {/* Popover lives at the component root so it can portal out of
          the alignment row without inheriting any of its overflow /
          z-index quirks. Triangle shapes render the 3-input variant;
          everything else (rect, image, checkbox, etc.) uses the
          4-input default. */}
      {isTriangle ? (
        <CornerRadiusPopover
          variant="triangle"
          anchorEl={cornerAnchorEl}
          onClose={() => setCornerAnchorEl(null)}
          values={triangleCornerValues}
          onCornersChange={handleTriangleCornersChange}
        />
      ) : (
        <CornerRadiusPopover
          anchorEl={cornerAnchorEl}
          onClose={() => setCornerAnchorEl(null)}
          values={cornerValues}
          onCornersChange={handleCornersChange}
        />
      )}
    </div>
  );
};

export default RotateComponent;
